import {useExchangeRates} from '#/hooks/query/use-exchange-rates';
import {usePortfolioTicker} from '#/hooks/query/use-portfolio-ticker';
import {CoinDisplayPreferences, useCoinDisplayPreferences} from '#/hooks/use-coin-display-preferences';
import {Portfolio} from '#/hooks/use-portfolio';
import {decimalsMultiplier} from '#/utils/contract-consts';
import {roundDecimals} from '#/utils/math-utils';
import {useEffect, useState} from 'react';
import {GraphRange, Hex, PortfolioTickerEntry} from 'shareland-common';

export type PortfolioValueGraphData = {
  marketValue: number;
  date: number;
};

const tradingBarViewEntryToGraphData = (
  tradingBarViewEntry: PortfolioTickerEntry,
  displayPref: CoinDisplayPreferences,
): PortfolioValueGraphData => {
  const {date, marketPrice, usdcToUsdExchangeRate} = tradingBarViewEntry;

  return {
    marketValue: roundDecimals(
      displayPref.usdcToNumericDisplayAmountWithUsdExchangeRate(marketPrice, usdcToUsdExchangeRate) /
        decimalsMultiplier,
      2,
    ),
    date: new Date(date).getTime(),
  };
};

export const usePortfolioValueGraphData = (graphRange: GraphRange, account: Hex, portfolio: Portfolio) => {
  const displayPref = useCoinDisplayPreferences();

  const {data = [], ...rest} = usePortfolioTicker(graphRange, account);
  const {data: exchangeRate} = useExchangeRates();

  const [now, setNow] = useState(new Date());
  useEffect(() => {
    setNow(new Date());
  }, [graphRange, setNow]);

  const lastDataElement = data.at(-1);

  return {
    data:
      data.length === 0
        ? []
        : data
            .slice(0, lastDataElement && lastDataElement.date < now ? data.length : data.length - 1)
            .concat({
              marketPrice: portfolio.totalAssetsValue,
              date: now,
              usdcToUsdExchangeRate: exchangeRate?.usdcToUsd ?? 1,
            })
            .map(entry => tradingBarViewEntryToGraphData(entry, displayPref)),
    ...rest,
  };
};
